import { SxProps } from "@mui/material";
import { TRACK_EVENTS } from "core/consts";
import { GlossarySection } from "core/types";
import LinkV2, { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import {
  margin as computedMargin,
  dp,
  important,
  translate,
} from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_13,
  FONT_SIZE_14,
} from "ds_legacy/materials/typography";
import { useProvidersearchNavigationUrls } from "dsl/hooks/useNavigationHandlers";
import { ExternalLinkIcon } from "lucide-react";
import { CSSProperties } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import { FunnelSteps } from "../ProviderSearchOnboarding/common";

export const LinktoGlossary = ({
  funnelStep,
  linkSx,
  margin = computedMargin(0, 0.25),
  section,
  short,
}: {
  funnelStep?: FunnelSteps;
  linkSx?: SxProps;
  margin?: CSSProperties["margin"];
  section?: GlossarySection;
  short?: boolean;
}) => {
  const translations = useTranslations();
  const { goToGlossary } = useProvidersearchNavigationUrls();
  const externalLinkLabel = useExternalLinkLabel();
  const { trackEvent } = useTracking();

  const ariaLabel = externalLinkLabel(
    translations.providersearch.glossary.glossaryAriaLabel,
  );

  const handleClick = () => {
    if (funnelStep)
      trackEvent({
        name: TRACK_EVENTS.PROVIDER_SEARCH_FUNNEL_GLOSSARY_CLICKED,
        step: funnelStep,
      });
  };

  if (short) {
    return (
      <LinkV2
        aria-label={ariaLabel}
        sx={{
          alignItems: "baseline",
          display: "inline-flex",
          fontSize: FONT_SIZE_14,
          margin,
          ...linkSx,
        }}
        target="_blank"
        to={goToGlossary(section)}
        onClick={handleClick}
      >
        <ExternalLinkIcon
          fontSize="inherit"
          size={FONT_SIZE_13}
          style={{
            color: "inherit",
            height: important(dp(13)),
            paddingRight: dp(2),
            transform: translate({ y: 1 }),
          }}
        />
        {translations.providersearch.header.glossary}
      </LinkV2>
    );
  }

  return (
    <TranslationComposition
      translations={translations.providersearch.glossary.linkToGlossary}
      withOptions
    >
      {([before, link, after]) => (
        <Body
          as="p"
          margin={margin}
          style={{
            alignSelf: "flex-start",
          }}
        >
          {before}
          <LinkV2
            aria-label={ariaLabel}
            sx={{
              alignItems: "baseline",
              display: "inline-flex",
              margin: computedMargin(0, 0.25),
              whiteSpace: "nowrap",
              ...linkSx,
            }}
            target="_blank"
            to={goToGlossary(section)}
            onClick={handleClick}
          >
            <ExternalLinkIcon
              fontSize="inherit"
              size={FONT_SIZE_13}
              style={{
                color: "inherit",
                height: important(dp(13)),
                paddingRight: dp(2),
                transform: translate({ y: 1 }),
              }}
            />
            {link}
          </LinkV2>
          {after}
        </Body>
      )}
    </TranslationComposition>
  );
};
